import type { CustomFieldValues } from '@web/modules/custom-fields/contract';
import type {
  CustomerSituation,
  CustomerType,
  DuplicateMatch,
  OriginChannel,
} from '@web/modules/customers/contract';

/**
 * The lead pool contract (docs/specs/020): a lead is a record of interest — who, what and
 * where from — in the pool of its unit until someone pulls it into a pipeline.
 */
export const LEAD_STATUSES = ['available', 'in_opportunity', 'won', 'discarded'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_ENTRIES = [
  'manual',
  'import',
  'form',
  'api',
  'conversation',
  'lost_opportunity',
] as const;
export type LeadEntry = (typeof LEAD_ENTRIES)[number];

export const LEAD_LIMITS = {
  nameMax: 160,
  companyMax: 160,
  interestMax: 200,
  interestItemsMax: 20,
  messageMax: 5000,
  discardReasonMax: 200,
  pageSize: 25,
} as const;

export interface LeadOutcome {
  dealId: string;
  pipelineName: string;
  lostReason: string | null;
  at: string;
  byMembershipId: string | null;
}

export interface LeadSummary {
  id: string;
  type: CustomerType;
  label: string;
  name: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
  document: string | null;
  interest: string | null;
  /** The catalog items of interest that still exist. */
  interestItems: { id: string; name: string }[];
  entry: LeadEntry;
  origin: { channel: OriginChannel | null; detail: string | null };
  source: { id: string; name: string } | null;
  unit: { id: string; name: string };
  status: LeadStatus;
  attempts: number;
  lastOutcome: LeadOutcome | null;
  discardReason: string | null;
  person: { id: string; displayName: string; situation: CustomerSituation } | null;
  /** The contact (the person) it is about: a link instead of a copy of the name (spec 033). */
  contact: { id: string; name: string } | null;
  /** Other open leads (pool or opportunity) that look like the same person. */
  samePersonCount: number;
  currentDealId: string | null;
  receivedAt: string;
  pulledAt: string | null;
  closedAt: string | null;
}

export interface LeadSibling {
  id: string;
  label: string;
  interest: string | null;
  status: LeadStatus;
  receivedAt: string;
}

export interface LeadOpportunity {
  dealId: string;
  title: string;
  status: 'open' | 'won' | 'lost';
  archived: boolean;
  pipelineName: string;
  stageName: string;
  lostReason: string | null;
  ownerName: string | null;
  /** Whether the caller's data scope reaches the deal (a seller's is out of an SDR's sight). */
  canOpen: boolean;
  primary: boolean;
  linkedAt: string;
  closedAt: string | null;
}

export interface LeadDetails extends LeadSummary {
  message: string | null;
  /** The values of the organization's lead fields, by key. */
  customFields: CustomFieldValues;
  siblings: LeadSibling[];
  opportunities: LeadOpportunity[];
  /** The campaign it came from (`campaign`, `content`, `term`, `source`, `medium`). */
  utm: Record<string, string> | null;
  /** What came and fits no field (a lead form's ids, a number as sent), by its name. */
  rawData: Record<string, string>;
  /** The import that brought it. */
  importJobId: string | null;
  /** The conversation identity it came from (entry `conversation`). */
  identityId: string | null;
}

export interface LeadList {
  items: LeadSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LeadListQuery {
  status?: LeadStatus;
  q?: string;
  unitId?: string;
  entry?: LeadEntry;
  /** The origin channel (spec 025). */
  channel?: OriginChannel;
  /** A campaign (`utm_campaign`) in any origin touch of the lead. */
  campaign?: string;
  worked?: 'never' | 'yes';
  customer?: true;
  /** The leads of a person (the customer's page). */
  customerId?: string;
  /** The leads of one person (spec 064). */
  contactId?: string;
  /** The leads an opportunity had (the deal's page). */
  dealId?: string;
  page?: number;
  pageSize?: number;
}

export interface LeadInput {
  type?: CustomerType;
  name?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  interest?: string | null;
  interestItemIds?: string[];
  message?: string | null;
  originChannel?: OriginChannel | null;
  originDetail?: string | null;
  unitId?: string;
  customFields?: CustomFieldValues;
}

export interface PullLeadInput {
  pipelineId?: string;
  stageId?: string;
  title?: string;
  valueCents?: number;
  customerId?: string;
  newCustomer?: true;
  withLeadIds?: string[];
  /** The seller who takes it (an SDR's handoff): an active member of the lead's unit. */
  ownerMembershipId?: string;
}

/**
 * "Nova oportunidade" for someone not registered (spec 021): the lead — a name and, usually, a
 * phone or an e-mail — is registered and pulled at once (`POST /leads/opportunity`).
 */
export interface CreateLeadOpportunityInput {
  lead: {
    name: string;
    phone?: string;
    email?: string;
    company?: string;
    interest?: string;
    originChannel?: string | null;
    originDetail?: string;
    unitId?: string;
  };
  opportunity: {
    pipelineId?: string;
    stageId?: string;
    title?: string;
    valueCents?: number;
    expectedCloseDate?: string;
    customFields?: Record<string, unknown>;
    /** `null` = nobody yet; absent = the caller. */
    ownerMembershipId?: string | null;
  };
}

/** The pool's bulk actions (spec 020, rule 24). */
export type BulkLeadsInput =
  | { action: 'pull'; ids: string[]; pipelineId?: string; stageId?: string }
  | { action: 'discard'; ids: string[]; reason: string };

export interface BulkLeadsResult {
  done: number;
  failed: number;
  items: { id: string; ok: boolean; code: string | null; dealId: string | null }[];
}

export interface PullLeadResult {
  dealId: string;
  lead: LeadDetails;
}

export type LeadPersonMatch = DuplicateMatch;

/** What the lead is interested in, in words: the typed text and the catalog items. */
export function interestText(lead: Pick<LeadSummary, 'interest' | 'interestItems'>): string {
  return [lead.interest, ...lead.interestItems.map((item) => item.name)]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The opportunity's title when none is typed: `<interest> — <name>` (the typed interest, else
 * the first catalog item), or the name.
 */
export function defaultOpportunityTitle(
  lead: Pick<LeadSummary, 'interest' | 'interestItems' | 'label'>,
): string {
  const interest = lead.interest ?? lead.interestItems[0]?.name;
  return (interest ? `${interest} — ${lead.label}` : lead.label).slice(0, 160);
}
